import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  useTheme,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { getNewHires, NewHire } from '../../services/hireService';
import { getTeamMemberHires, getOwnerMemberHires } from '../../services/ownerTeamHireService';
import { ownerService } from '../../services/ownerService';
import { progressStageService, ProgressStage } from '../../services/progressStageService';

interface AnalyticsProps {
  ownerProfile?: any;
}

const Analytics: React.FC<AnalyticsProps> = ({ ownerProfile }) => {
  const [hires, setHires] = useState<NewHire[]>([]);
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeamMember, setSelectedTeamMember] = useState<any>(null);
  const [filteredHires, setFilteredHires] = useState<NewHire[]>([]);
  const theme = useTheme();
  const [progressStages, setProgressStages] = useState<ProgressStage[]>([]);

  // Filter team members to only include admins (exclude owners) for buttons
  const adminTeamMembers = teamMembers.filter(member => member.role === 'admin');
  const ownerTeamMembers = teamMembers.filter(member => member.role === 'owner');

  useEffect(() => {
    fetchData();
  }, []);

  // Update filtered hires when hires or selected team member changes
  useEffect(() => {
    if (selectedTeamMember) {
      // Filter hires based on selected team member
      const teamMemberEmail = selectedTeamMember.team_member_email || selectedTeamMember.email;
      const filtered = hires.filter(hire => {
        if (!hire.recruiter_emails) return false;
        
        // Convert to string and check if it contains the team member email
        const recruiterEmailsStr = String(hire.recruiter_emails);
        return recruiterEmailsStr.toLowerCase().includes(teamMemberEmail.toLowerCase());
      });
      setFilteredHires(filtered);
    } else {
      // Show all hires when no team member is selected
      setFilteredHires(hires);
    }
  }, [hires, selectedTeamMember]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch progress stages for current user's organization (DB override if present)
      const stages = await progressStageService.getCurrentUserProgressStagesWithDbOverride();
      setProgressStages(stages);
      
      // Fetch organization team members
      const orgTeamMembers = await ownerService.getOrganizationTeamMembers();
      setTeamMembers(orgTeamMembers);

      // Fetch all hires
      const data = await getNewHires();
      setHires(data);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTeamMemberClick = async (member: any) => {
    try {
      setLoading(true);
      console.log('Team member clicked:', member.full_name);
      
      if (selectedTeamMember?.id === member.id) {
        // If clicking the same team member, deselect them
        setSelectedTeamMember(null);
        setFilteredHires(hires);
      } else {
        // Select the new team member and fetch their hires
        setSelectedTeamMember(member);
        
        // Use the team member service to get filtered hires
        const teamMemberEmail = member.team_member_email || member.email;
        const teamMemberHires = await getTeamMemberHires(teamMemberEmail);
        setFilteredHires(teamMemberHires);
      }
    } catch (error) {
      console.error('Error fetching team member hires:', error);
      // Fallback to client-side filtering
      const teamMemberEmail = member.team_member_email || member.email;
      const filtered = hires.filter(hire => {
        if (!hire.recruiter_emails) return false;
        
        // Convert to string and check if it contains the team member email
        const recruiterEmailsStr = String(hire.recruiter_emails);
        return recruiterEmailsStr.toLowerCase().includes(teamMemberEmail.toLowerCase());
      });
      setFilteredHires(filtered);
      setSelectedTeamMember(member);
    } finally {
      setLoading(false);
    }
  };

  const handleOwnerMemberClick = async (member: any) => {
    try {
      setLoading(true);
      console.log('Owner member clicked:', member.full_name);
      
      if (selectedTeamMember?.id === member.id) {
        // If clicking the same owner member, deselect them
        setSelectedTeamMember(null);
        setFilteredHires(hires);
      } else {
        // Select the new owner member and fetch their hires
        setSelectedTeamMember(member);
        
        // Use the owner member service to get filtered hires
        const ownerMemberEmail = member.team_member_email || member.email;
        const ownerMemberHires = await getOwnerMemberHires(ownerMemberEmail);
        setFilteredHires(ownerMemberHires);
      }
    } catch (error) {
      console.error('Error fetching owner member hires:', error);
      // Fallback to client-side filtering
      const ownerMemberEmail = member.team_member_email || member.email;
      const filtered = hires.filter(hire => {
        if (!hire.recruiter_emails) return false;
        
        // Convert to string and check if it contains the owner member email
        const recruiterEmailsStr = String(hire.recruiter_emails);
        return recruiterEmailsStr.toLowerCase().includes(ownerMemberEmail.toLowerCase());
      });
      setFilteredHires(filtered);
      setSelectedTeamMember(member);
    } finally {
      setLoading(false);
    }
  };

  const handleShowAllHires = () => {
    setSelectedTeamMember(null);
    setFilteredHires(hires);
  };

  // Calculate analytics data
  const getStageCounts = (hires: NewHire[]) => {
    const counts = progressStages.map(stage => ({
      ...stage,
      count: hires.filter(hire => hire.progress === stage.value).length,
      percentage: hires.length > 0 ? (hires.filter(hire => hire.progress === stage.value).length / hires.length) * 100 : 0
    }));
    return counts;
  };

  const stageCounts = getStageCounts(filteredHires);
  const totalHires = filteredHires.length;
  const lastStageValue = progressStages.length > 0 ? progressStages[progressStages.length - 1].value : 6;
  const completedHires = filteredHires.filter(hire => hire.progress === lastStageValue).length;
  const inProgressHires = totalHires - completedHires;

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #2a2a2a 0%, #3a3a3a 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Analytics Dashboard
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9 }}>
          {selectedTeamMember 
            ? `Analytics for ${selectedTeamMember.full_name}`
            : 'Organization-wide Analytics'
          }
        </Typography>
      </Paper>

      {/* Summary Cards */}
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: 3, 
        mb: 3,
        '& > *': {
          flex: '1 1 250px',
          minWidth: '250px',
        }
      }}>
        <Card
          elevation={3}
          sx={{
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 8px 25px rgba(0, 0, 0, 0.3)'
                : '0 8px 25px rgba(102, 126, 234, 0.2)',
            },
          }}
        >
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="h6">
                  Total Hires
                </Typography>
                <Typography variant="h4" component="div" sx={{ fontWeight: 700 }}>
                  {totalHires}
                </Typography>
              </Box>
              <PeopleIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
            </Box>
          </CardContent>
        </Card>

        <Card
          elevation={3}
          sx={{
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 8px 25px rgba(0, 0, 0, 0.3)'
                : '0 8px 25px rgba(102, 126, 234, 0.2)',
            },
          }}
        >
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="h6">
                  In Progress
                </Typography>
                <Typography variant="h4" component="div" sx={{ fontWeight: 700 }}>
                  {inProgressHires}
                </Typography>
              </Box>
              <ScheduleIcon sx={{ fontSize: 40, color: theme.palette.warning.main }} />
            </Box>
          </CardContent>
        </Card>

        <Card
          elevation={3}
          sx={{
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 8px 25px rgba(0, 0, 0, 0.3)'
                : '0 8px 25px rgba(102, 126, 234, 0.2)',
            },
          }}
        >
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="h6">
                  Completed
                </Typography>
                <Typography variant="h4" component="div" sx={{ fontWeight: 700 }}>
                  {completedHires}
                </Typography>
              </Box>
              <CheckCircleIcon sx={{ fontSize: 40, color: theme.palette.success.main }} />
            </Box>
          </CardContent>
        </Card>

        <Card
          elevation={3}
          sx={{
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 8px 25px rgba(0, 0, 0, 0.3)'
                : '0 8px 25px rgba(102, 126, 234, 0.2)',
            },
          }}
        >
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="h6">
                  Completion Rate
                </Typography>
                <Typography variant="h4" component="div" sx={{ fontWeight: 700 }}>
                  {totalHires > 0 ? Math.round((completedHires / totalHires) * 100) : 0}%
                </Typography>
              </Box>
              <TrendingUpIcon sx={{ fontSize: 40, color: theme.palette.info.main }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Filters - Same as Overview Tab */}
      <Paper elevation={3} sx={{ mb: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 2, 
          mb: 3,
          p: 2,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.06) 100%)'
            : 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
          borderRadius: 2,
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(102, 126, 234, 0.1)'}`,
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              width: '100%', 
              mb: 2,
              color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
              fontWeight: 600,
            }}
          >
            Filters:
          </Typography>
          <Button
            variant="contained"
            onClick={handleShowAllHires}
            sx={{
              background: selectedTeamMember 
                ? (theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)')
                : 'transparent',
              color: selectedTeamMember 
                ? '#ffffff'
                : (theme.palette.mode === 'dark' ? '#667eea' : '#667eea'),
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '8px',
              px: 3,
              py: 1,
              border: selectedTeamMember 
                ? 'none'
                : `2px solid ${theme.palette.mode === 'dark' ? '#667eea' : '#667eea'}`,
              boxShadow: selectedTeamMember
                ? (theme.palette.mode === 'dark'
                    ? '0 2px 10px rgba(102, 126, 234, 0.3)'
                    : '0 2px 10px rgba(102, 126, 234, 0.2)')
                : 'none',
              '&:hover': {
                background: selectedTeamMember
                  ? (theme.palette.mode === 'dark'
                      ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                      : 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)')
                  : (theme.palette.mode === 'dark'
                      ? 'rgba(102, 126, 234, 0.1)'
                      : 'rgba(102, 126, 234, 0.1)'),
                transform: 'translateY(-1px)',
                boxShadow: selectedTeamMember
                  ? (theme.palette.mode === 'dark'
                      ? '0 4px 15px rgba(102, 126, 234, 0.4)'
                      : '0 4px 15px rgba(102, 126, 234, 0.3)')
                  : 'none',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Show All Hires
          </Button>
          
          {/* Owner Team Members */}
          {ownerTeamMembers.length > 0 && (
            <>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  width: '100%', 
                  mt: 2, 
                  mb: 1,
                  color: theme.palette.mode === 'dark' ? '#b3b3b3' : theme.palette.text.secondary,
                  fontWeight: 500,
                  fontSize: '0.9rem',
                }}
              >
                Org Owner(s):
              </Typography>
              {ownerTeamMembers.map((member) => (
                <Button
                  key={member.id}
                  variant="contained"
                  onClick={() => handleOwnerMemberClick(member)}
                  sx={{
                    background: selectedTeamMember?.id === member.id
                      ? 'transparent'
                      : (theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'),
                    color: selectedTeamMember?.id === member.id
                      ? (theme.palette.mode === 'dark' ? '#667eea' : '#667eea')
                      : '#ffffff',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '8px',
                    px: 3,
                    py: 1,
                    border: selectedTeamMember?.id === member.id
                      ? `2px solid ${theme.palette.mode === 'dark' ? '#667eea' : '#667eea'}`
                      : 'none',
                    boxShadow: selectedTeamMember?.id === member.id
                      ? 'none'
                      : (theme.palette.mode === 'dark'
                          ? '0 2px 10px rgba(102, 126, 234, 0.3)'
                          : '0 2px 10px rgba(102, 126, 234, 0.2)'),
                    '&:hover': {
                      background: selectedTeamMember?.id === member.id
                        ? (theme.palette.mode === 'dark'
                            ? 'rgba(102, 126, 234, 0.1)'
                            : 'rgba(102, 126, 234, 0.1)')
                        : (theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                            : 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'),
                      transform: 'translateY(-1px)',
                      boxShadow: selectedTeamMember?.id === member.id
                        ? 'none'
                        : (theme.palette.mode === 'dark'
                            ? '0 4px 15px rgba(102, 126, 234, 0.4)'
                            : '0 4px 15px rgba(102, 126, 234, 0.3)'),
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {member.full_name}
                </Button>
              ))}
            </>
          )}
          
          {/* Admin Team Members */}
          {adminTeamMembers.length > 0 && (
            <>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  width: '100%', 
                  mt: 2, 
                  mb: 1,
                  color: theme.palette.mode === 'dark' ? '#b3b3b3' : theme.palette.text.secondary,
                  fontWeight: 500,
                  fontSize: '0.9rem',
                }}
              >
                Admins:
              </Typography>
              {adminTeamMembers.map((member) => (
                <Button
                  key={member.id}
                  variant="contained"
                  onClick={() => handleTeamMemberClick(member)}
                  sx={{
                    background: selectedTeamMember?.id === member.id
                      ? 'transparent'
                      : (theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'),
                    color: selectedTeamMember?.id === member.id
                      ? (theme.palette.mode === 'dark' ? '#667eea' : '#667eea')
                      : '#ffffff',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '8px',
                    px: 3,
                    py: 1,
                    border: selectedTeamMember?.id === member.id
                      ? `2px solid ${theme.palette.mode === 'dark' ? '#667eea' : '#667eea'}`
                      : 'none',
                    boxShadow: selectedTeamMember?.id === member.id
                      ? 'none'
                      : (theme.palette.mode === 'dark'
                          ? '0 2px 10px rgba(102, 126, 234, 0.3)'
                          : '0 2px 10px rgba(102, 126, 234, 0.2)'),
                    '&:hover': {
                      background: selectedTeamMember?.id === member.id
                        ? (theme.palette.mode === 'dark'
                            ? 'rgba(102, 126, 234, 0.1)'
                            : 'rgba(102, 126, 234, 0.1)')
                        : (theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                            : 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'),
                      transform: 'translateY(-1px)',
                      boxShadow: selectedTeamMember?.id === member.id
                        ? 'none'
                        : (theme.palette.mode === 'dark'
                            ? '0 4px 15px rgba(102, 126, 234, 0.4)'
                            : '0 4px 15px rgba(102, 126, 234, 0.3)'),
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {member.full_name}
                </Button>
              ))}
            </>
          )}
        </Box>
      </Paper>

      {/* Progress Chart */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          Hire Progress Through Stages
        </Typography>
        
        <Box sx={{ mb: 4 }}>
          {stageCounts.map((stage, index) => (
            <Box key={index} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 500, flex: 1 }}>
                  {stage.label}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    {stage.count} hires
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    ({stage.percentage.toFixed(1)}%)
                  </Typography>
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={stage.percentage}
                sx={{
                  height: 12,
                  borderRadius: 6,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 6,
                    background: stage.color,
                    boxShadow: `0 0 10px ${stage.color}40`,
                  },
                }}
              />
            </Box>
          ))}
        </Box>

        {/* Stage Distribution Summary */}
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 2,
          '& > *': {
            flex: '1 1 200px',
            minWidth: '200px',
          }
        }}>
          {stageCounts.map((stage, index) => (
            <Card
              key={index}
              elevation={2}
              sx={{
                p: 2,
                textAlign: 'center',
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.1) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 1) 100%)',
                border: `2px solid ${stage.color}20`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 20px ${stage.color}30`,
                },
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 700, color: stage.color }}>
                {stage.count}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                {stage.label}
              </Typography>
            </Card>
          ))}
        </Box>
      </Paper>
    </Container>
  );
};

export default Analytics; 